import { useMemo, useState } from "react";
import { evaluateFilter, type FilterOp } from "@jump-pipelines/core";
import { Button } from "./primitives/Button";
import { TextInput } from "./primitives/FormField";
import { filterFieldsForObjectType, filterFieldsForOutput } from "../lib/filterFields";

// FilterBuilder (DEV-4842) — an ANANSI-styled editor over the pipeline filter DSL.
// It emits `{}` when there are no conditions (back-compat no-op) and a well-formed
// DSL object otherwise. The live-test panel runs the SAME core evaluator the worker
// uses, so what you see here is what the pipeline will do.

type Action = "exclude" | "include";
type Match = "all" | "any";
type Source = "event" | "output";

interface UiCondition {
  field: string;
  op: FilterOp;
  value: string;
  caseInsensitive: boolean;
}

const OPS: Array<{ value: FilterOp; label: string }> = [
  { value: "equals", label: "equals" },
  { value: "notEquals", label: "does not equal" },
  { value: "contains", label: "contains" },
  { value: "startsWith", label: "starts with" },
  { value: "endsWith", label: "ends with" },
  { value: "matchesRegex", label: "matches regex" },
  { value: "in", label: "is one of" },
  { value: "exists", label: "is present" },
];

const CUSTOM = "__custom__";

const rowStyle: React.CSSProperties = {
  display: "flex",
  gap: 8,
  alignItems: "flex-start",
  padding: "12px",
  border: "1px solid var(--color-border)",
  background: "var(--color-surface)",
  marginBottom: 8,
};

const selectStyle: React.CSSProperties = { minWidth: 0, flex: "1 1 0" };

function opTakesValue(op: FilterOp): boolean {
  return op !== "exists";
}

// ─── DSL ⇄ UI-state conversion ───────────────────────────────────────────────

function toUiConditions(value: Record<string, unknown>): UiCondition[] {
  const conditions = Array.isArray((value as { conditions?: unknown }).conditions)
    ? ((value as { conditions: unknown[] }).conditions)
    : [];
  return conditions.map((raw) => {
    const c = (raw ?? {}) as Record<string, unknown>;
    const rawValue = c.value;
    return {
      field: typeof c.field === "string" ? c.field : "",
      op: (typeof c.op === "string" ? c.op : "equals") as FilterOp,
      value: Array.isArray(rawValue) ? rawValue.join(", ") : rawValue == null ? "" : String(rawValue),
      caseInsensitive: c.caseInsensitive === true,
    };
  });
}

function coerceValue(op: FilterOp, raw: string): string | string[] {
  if (op === "in") {
    return raw.split(",").map((s) => s.trim()).filter((s) => s.length > 0);
  }
  return raw;
}

function toDsl(action: Action, match: Match, source: Source, conditions: UiCondition[]): Record<string, unknown> {
  // Drop conditions with a blank field (e.g. "Custom…" left empty): an empty path
  // resolves to nothing and would silently never match, making the filter a no-op
  // the operator believes is active. No valid conditions → `{}` (no filter).
  const valid = conditions.filter((c) => c.field.trim().length > 0);
  if (valid.length === 0) return {};
  return {
    version: 1,
    action,
    match,
    // Omit the default so an event-scoped filter stays a minimal object.
    ...(source === "output" ? { source } : {}),
    conditions: valid.map((c) => {
      const base: Record<string, unknown> = { field: c.field, op: c.op };
      if (opTakesValue(c.op)) base.value = coerceValue(c.op, c.value);
      if (c.caseInsensitive) base.caseInsensitive = true;
      return base;
    }),
  };
}

export function FilterBuilder({
  value,
  onChange,
  sourceObjectType,
  destObjectType,
}: {
  value: Record<string, unknown>;
  onChange: (f: Record<string, unknown>) => void;
  sourceObjectType?: string;
  destObjectType?: string;
}) {
  const action: Action = (value.action as Action) ?? "exclude";
  const match: Match = (value.match as Match) ?? "any";
  const source: Source = (value.source as Source) ?? "event";
  const conditions = useMemo(() => toUiConditions(value), [value]);

  // Field suggestions follow the source: raw event fields, or the transform's
  // enriched output fields (e.g. properties.jump_offer_name).
  const curated = source === "output"
    ? filterFieldsForOutput(destObjectType)
    : filterFieldsForObjectType(sourceObjectType);

  function emit(nextAction: Action, nextMatch: Match, nextSource: Source, nextConditions: UiCondition[]) {
    onChange(toDsl(nextAction, nextMatch, nextSource, nextConditions));
  }

  function setAction(a: Action) { emit(a, match, source, conditions); }
  function setMatch(m: Match) { emit(action, m, source, conditions); }
  function setSource(s: Source) { emit(action, match, s, conditions); }

  function addCondition() {
    const first = curated[0]?.path ?? "";
    emit(action, match, source, [...conditions, { field: first, op: "equals", value: "", caseInsensitive: false }]);
  }
  function removeCondition(idx: number) {
    emit(action, match, source, conditions.filter((_, i) => i !== idx));
  }
  function updateCondition(idx: number, patch: Partial<UiCondition>) {
    emit(action, match, source, conditions.map((c, i) => (i === idx ? { ...c, ...patch } : c)));
  }

  return (
    <div>
      <p className="page-help" style={{ marginTop: 0 }}>
        Drop events before they sync. Leave empty to sync everything.
      </p>

      {conditions.length > 0 && (
        <div style={{ display: "flex", gap: 24, marginBottom: 12, flexWrap: "wrap" }}>
          <Segmented
            label="WHEN AN EVENT MATCHES"
            value={action}
            options={[{ value: "exclude", label: "DROP IT" }, { value: "include", label: "KEEP ONLY IT" }]}
            onChange={(v) => setAction(v as Action)}
          />
          <Segmented
            label="MATCH"
            value={match}
            options={[{ value: "any", label: "ANY" }, { value: "all", label: "ALL" }]}
            onChange={(v) => setMatch(v as Match)}
          />
          <Segmented
            label="MATCH FIELDS ON"
            value={source}
            options={[{ value: "event", label: "INCOMING EVENT" }, { value: "output", label: "TRANSFORMED OUTPUT" }]}
            onChange={(v) => setSource(v as Source)}
          />
        </div>
      )}
      {conditions.length > 0 && source === "output" && (
        <p className="field__hint" style={{ marginTop: -4, marginBottom: 12 }}>
          Matches the transformed record (enriched fields like the offer name), evaluated after the transform runs.
        </p>
      )}

      {conditions.map((c, idx) => {
        const isCustom = !curated.some((f) => f.path === c.field);
        return (
          <div key={idx} style={rowStyle}>
            <div style={{ ...selectStyle, display: "flex", flexDirection: "column", gap: 8 }}>
              <select
                className="field__input"
                value={isCustom ? CUSTOM : c.field}
                onChange={(e) => {
                  const v = e.target.value;
                  updateCondition(idx, { field: v === CUSTOM ? "" : v });
                }}
                aria-label="Field"
              >
                {curated.map((f) => (
                  <option key={f.path} value={f.path}>{f.label}</option>
                ))}
                <option value={CUSTOM}>Custom…</option>
              </select>
              {isCustom && (
                <TextInput
                  value={c.field}
                  placeholder="payload.eventId"
                  onChange={(e) => updateCondition(idx, { field: e.target.value })}
                  aria-label="Custom field path"
                  style={{ fontFamily: "var(--font-mono)" }}
                />
              )}
            </div>

            <select
              className="field__input"
              style={selectStyle}
              value={c.op}
              onChange={(e) => updateCondition(idx, { op: e.target.value as FilterOp })}
              aria-label="Operator"
            >
              {OPS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>

            {opTakesValue(c.op) && (
              <div style={{ ...selectStyle, display: "flex", flexDirection: "column", gap: 4 }}>
                <TextInput
                  value={c.value}
                  placeholder="value"
                  onChange={(e) => updateCondition(idx, { value: e.target.value })}
                  aria-label="Value"
                />
                {c.op === "in" && <span className="field__hint">comma-separated</span>}
              </div>
            )}

            <label
              className="uppercase-label"
              style={{ display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap", paddingTop: 8 }}
            >
              <input
                type="checkbox"
                checked={c.caseInsensitive}
                onChange={(e) => updateCondition(idx, { caseInsensitive: e.target.checked })}
              />
              IGNORE CASE
            </label>

            <Button type="button" variant="ghost" onClick={() => removeCondition(idx)} aria-label="Remove condition">
              ✕
            </Button>
          </div>
        );
      })}

      <Button type="button" variant="secondary" onClick={addCondition}>ADD CONDITION</Button>

      <LiveTest value={value} />
    </div>
  );
}

// ─── Segmented toggle (ANANSI: solid = active, bordered = inactive) ───────────

function Segmented({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: Array<{ value: string; label: string }>;
  onChange: (v: string) => void;
}) {
  return (
    <div>
      <div className="uppercase-label" style={{ marginBottom: 6 }}>{label}</div>
      <div style={{ display: "flex", gap: 0, border: "1px solid var(--color-border)", width: "fit-content" }}>
        {options.map((o) => (
          <button
            key={o.value}
            type="button"
            onClick={() => onChange(o.value)}
            className="uppercase-label"
            style={{
              padding: "6px 12px",
              cursor: "pointer",
              border: "none",
              background: value === o.value ? "var(--color-primary)" : "transparent",
              color: value === o.value ? "var(--color-text)" : "var(--color-text-secondary)",
            }}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Live test panel — runs the SAME core evaluator the worker uses ──────────

function LiveTest({ value }: { value: Record<string, unknown> }) {
  const [sample, setSample] = useState("");
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);

  function run() {
    let parsed: unknown;
    try {
      parsed = JSON.parse(sample);
    } catch {
      setResult({ ok: false, text: "invalid JSON" });
      return;
    }
    const outcome = evaluateFilter(value, parsed as never);
    setResult(
      outcome.skip
        ? { ok: false, text: `SKIP — ${outcome.reason ?? "filtered"}` }
        : { ok: true, text: "PASS" },
    );
  }

  return (
    <div style={{ marginTop: 24, borderTop: "1px solid var(--color-border)", paddingTop: 16 }}>
      <div className="uppercase-label" style={{ marginBottom: 6 }}>TEST AGAINST A SAMPLE EVENT</div>
      <textarea
        className="field__input field__input--multiline"
        rows={5}
        spellCheck={false}
        placeholder='{"payload":{"eventId":"TXR-9"}}'
        value={sample}
        onChange={(e) => setSample(e.target.value)}
        style={{ fontFamily: "var(--font-mono)", width: "100%" }}
      />
      <div style={{ display: "flex", gap: 12, alignItems: "center", marginTop: 8 }}>
        <Button type="button" variant="secondary" onClick={run}>TEST</Button>
        {result && (
          <span
            className="uppercase-label"
            style={{
              fontFamily: "var(--font-mono)",
              color: result.ok ? "var(--color-text-accent)" : "var(--color-secondary)",
            }}
          >
            {result.text}
          </span>
        )}
      </div>
    </div>
  );
}
